// GS Mandal — Vendor Portal Layout with left sidebar.
// Wraps all /vendor/portal/* pages. Fetches the vendor's `me` payload once
// and exposes it via React context so child pages can read + refresh it.

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import vendorApi, { VENDOR_TOKEN_KEY } from "@/lib/vendorApi";
import {
  Loader2, LogOut, LayoutDashboard, FolderUp, FileText,
  CreditCard, KeyRound, Building2,
} from "lucide-react";

const VendorContext = createContext(null);
export const useVendor = () => useContext(VendorContext);

const NAV = [
  { to: "", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "vnav-dashboard" },
  { to: "documents", label: "Upload Documents", icon: FolderUp, testid: "vnav-documents" },
  { to: "tenders", label: "Relevant Tenders", icon: FileText, testid: "vnav-tenders" },
  { to: "payment", label: "Registration Fee", icon: CreditCard, testid: "vnav-payment" },
  { to: "account", label: "Account & Security", icon: KeyRound, testid: "vnav-account" },
];

export default function VendorLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const [{ data: meData }, { data: pay }] = await Promise.all([
        vendorApi.get("/vendor/auth/me"),
        vendorApi.get("/vendor/payments/status").catch(() => ({ data: null })),
      ]);
      setMe(meData);
      setPayment(pay);
    } catch {
      // interceptor handles 401 redirect
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(VENDOR_TOKEN_KEY)) {
      navigate("/vendor/login", { replace: true });
      return;
    }
    reload();
  }, [navigate, reload]);

  const logout = () => {
    localStorage.removeItem(VENDOR_TOKEN_KEY);
    navigate("/vendor/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <div className="flex items-center gap-2 text-[#0D5C63]"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      </div>
    );
  }

  const reg = me?.registration || {};
  const mustChange = !!me?.must_change_password;

  return (
    <VendorContext.Provider value={{ me, reg, payment, mustChange, reload }}>
      <div data-testid="vendor-portal-page" className="min-h-screen bg-[#FAFAF7] flex">
        {/* Sidebar */}
        <aside className="hidden md:flex md:flex-col md:w-64 shrink-0 bg-gradient-to-b from-[#0D5C63] to-[#083E43] text-white">
          <div className="px-5 py-6 flex items-center gap-3 border-b border-white/10">
            <div className="w-10 h-10 bg-[#F26419] rounded-full flex items-center justify-center"><Building2 size={18} /></div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F26419] font-bold">GS Mandal</div>
              <div className="text-sm font-semibold">Vendor Portal</div>
            </div>
          </div>
          <div className="px-4 py-4 border-b border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Signed in as</div>
            <div className="text-xs font-semibold truncate" title={me?.email} data-testid="vendor-portal-email">{me?.email}</div>
            <div className="text-[10px] text-white/60 mt-0.5 truncate" title={reg.applicant_firm_name}>{reg.applicant_firm_name}</div>
          </div>
          <nav className="flex-1 py-4 overflow-y-auto">
            {NAV.map((n) => {
              const Icon = n.icon;
              const to = `/vendor/portal${n.to ? `/${n.to}` : ""}`;
              return (
                <NavLink
                  key={n.testid}
                  to={to}
                  end={n.end}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors ${
                      isActive
                        ? "bg-white/10 border-[#F26419] text-white font-semibold"
                        : "border-transparent text-white/80 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <Icon size={15} />
                  <span>{n.label}</span>
                </NavLink>
              );
            })}
          </nav>
          <button
            type="button"
            onClick={logout}
            data-testid="vendor-portal-logout"
            className="mx-4 mb-4 inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-2 rounded-md"
          >
            <LogOut size={12} /> Logout
          </button>
        </aside>

        {/* Mobile top bar */}
        <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-gradient-to-r from-[#0D5C63] to-[#083E43] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 size={16} />
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[#F26419] font-bold">GS Mandal</div>
              <div className="text-xs font-semibold">Vendor Portal</div>
            </div>
          </div>
          <button type="button" onClick={logout} className="text-[10px] font-bold uppercase tracking-widest bg-white/10 border border-white/20 px-3 py-1.5 rounded-md">Logout</button>
        </div>

        {/* Mobile bottom tab bar */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E9E2D2] grid grid-cols-5">
          {NAV.map((n) => {
            const Icon = n.icon;
            const to = `/vendor/portal${n.to ? `/${n.to}` : ""}`;
            const active = (n.end && location.pathname === "/vendor/portal") || (!n.end && location.pathname.startsWith(to));
            return (
              <NavLink key={n.testid} to={to} end={n.end} data-testid={`${n.testid}-m`} className={`flex flex-col items-center justify-center py-2 text-[9px] ${active ? "text-[#0D5C63] font-semibold" : "text-[#8a8478]"}`}>
                <Icon size={16} />
                <span className="mt-0.5">{n.label.split(" ")[0]}</span>
              </NavLink>
            );
          })}
        </nav>

        <main className="flex-1 min-w-0 pb-24 md:pb-8 pt-16 md:pt-0">
          {mustChange && location.pathname !== "/vendor/portal/account" && (
            <div className="bg-[#F5EFE3] border-b border-[#F26419]/40 px-5 py-3 text-sm text-[#1A1A1A]" data-testid="vendor-must-change-banner">
              You are using a temporary password. <NavLink to="/vendor/portal/account" className="font-bold underline text-[#0D5C63]">Change it now</NavLink> to unlock document uploads and payment.
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </VendorContext.Provider>
  );
}
